<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import SuporteAmshiftAPI from 'dashboard/api/suporteAmshift';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampoDeAnexos from './CampoDeAnexos.vue';
import { useAnexos } from './useAnexos';
import {
  anexosDoRascunho,
  camposDoRascunho as campos,
  limparRascunho,
} from './rascunho';
import {
  formatarDataHora,
  mensagemDoErro,
  montarFormDataDoChamado,
} from './helpers';

const emit = defineEmits(['acompanhar']);

const PRIORIDADE_PADRAO = 'media';

const { t, locale } = useI18n();
const route = useRoute();
const { anexos, recusas, adicionar, remover, aoColar } =
  useAnexos(anexosDoRascunho);

const catalogo = ref({ tipos: [], prioridades: [] });
const carregandoCatalogo = ref(true);
const enviando = ref(false);
const erro = ref('');
const aberto = ref(null);

const opcoesDeTipo = computed(() =>
  catalogo.value.tipos.map(tipo => ({ value: tipo.codigo, label: tipo.nome }))
);
const opcoesDePrioridade = computed(() =>
  catalogo.value.prioridades.map(prioridade => ({
    value: prioridade.codigo,
    label: prioridade.nome,
  }))
);
const descricaoDoTipo = computed(
  () =>
    catalogo.value.tipos.find(tipo => tipo.codigo === campos.tipo)?.descricao
);
const preenchido = computed(
  () =>
    campos.tipo && campos.titulo.trim().length >= 3 && campos.descricao.trim()
);
const prazoDaPrimeiraResposta = computed(() =>
  formatarDataHora(aberto.value?.sla?.resposta?.prazoEm, locale.value)
);

const carregarCatalogo = async () => {
  try {
    const { data } = await SuporteAmshiftAPI.catalogo();
    catalogo.value = data;
    if (!campos.prioridade) {
      const padrao = data.prioridades.find(p => p.codigo === PRIORIDADE_PADRAO);
      campos.prioridade = padrao?.codigo || data.prioridades[0]?.codigo || '';
    }
  } catch (e) {
    erro.value = mensagemDoErro(e, t('SUPORTE_AMSHIFT.ERRO.GENERICO'));
  } finally {
    carregandoCatalogo.value = false;
  }
};

const enviar = async () => {
  if (!preenchido.value) {
    erro.value = t('SUPORTE_AMSHIFT.NOVO.OBRIGATORIOS');
    return;
  }
  enviando.value = true;
  erro.value = '';
  try {
    const formData = montarFormDataDoChamado(
      { ...campos, tela: route.fullPath },
      anexos.value
    );
    const { data } = await SuporteAmshiftAPI.abrirChamado(formData);
    aberto.value = data;
    limparRascunho();
  } catch (e) {
    erro.value = mensagemDoErro(e, t('SUPORTE_AMSHIFT.ERRO.GENERICO'));
  } finally {
    enviando.value = false;
  }
};

const abrirOutro = () => {
  aberto.value = null;
};

onMounted(carregarCatalogo);
</script>

<template>
  <div
    v-if="aberto"
    class="flex flex-col items-center gap-3 py-6 text-center"
    data-test="chamado-aberto"
  >
    <span class="i-lucide-circle-check-big size-10 text-n-teal-11" />
    <h4 class="mb-0 text-base font-medium text-n-slate-12">
      {{ t('SUPORTE_AMSHIFT.NOVO.SUCESSO', { numero: aberto.numero }) }}
    </h4>
    <p v-if="prazoDaPrimeiraResposta" class="mb-0 text-sm text-n-slate-11">
      {{
        t('SUPORTE_AMSHIFT.NOVO.PRIMEIRA_RESPOSTA', {
          prazo: prazoDaPrimeiraResposta,
        })
      }}
    </p>
    <div class="flex gap-2">
      <Button
        type="button"
        :label="t('SUPORTE_AMSHIFT.NOVO.ACOMPANHAR')"
        @click="emit('acompanhar', aberto.numero)"
      />
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('SUPORTE_AMSHIFT.NOVO.OUTRO')"
        @click="abrirOutro"
      />
    </div>
  </div>

  <div v-else-if="carregandoCatalogo" class="flex justify-center py-8">
    <Spinner />
  </div>

  <div v-else class="flex flex-col gap-4">
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('SUPORTE_AMSHIFT.NOVO.TIPO') }}
        <Select
          v-model="campos.tipo"
          :options="opcoesDeTipo"
          :placeholder="t('SUPORTE_AMSHIFT.NOVO.TIPO_PLACEHOLDER')"
          class="!w-full"
          data-test="tipo"
        />
        <span
          v-if="descricaoDoTipo"
          class="text-xs font-normal text-n-slate-10"
        >
          {{ descricaoDoTipo }}
        </span>
      </label>
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('SUPORTE_AMSHIFT.NOVO.PRIORIDADE') }}
        <Select
          v-model="campos.prioridade"
          :options="opcoesDePrioridade"
          class="!w-full"
        />
      </label>
    </div>
    <Input
      v-model="campos.titulo"
      :label="t('SUPORTE_AMSHIFT.NOVO.TITULO')"
      :placeholder="t('SUPORTE_AMSHIFT.NOVO.TITULO_PLACEHOLDER')"
      data-test="titulo"
    />
    <div @paste="aoColar">
      <TextArea
        v-model="campos.descricao"
        :label="t('SUPORTE_AMSHIFT.NOVO.DESCRICAO')"
        :placeholder="t('SUPORTE_AMSHIFT.NOVO.DESCRICAO_PLACEHOLDER')"
        :max-length="20000"
        min-height="7rem"
        auto-height
        data-test="descricao"
      />
    </div>
    <CampoDeAnexos
      :anexos="anexos"
      :recusas="recusas"
      :disabled="enviando"
      @adicionar="adicionar"
      @remover="remover"
    />
    <p
      v-if="erro"
      class="px-3 py-2 mb-0 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      data-test="erro"
    >
      {{ erro }}
    </p>
    <Button
      type="button"
      icon="i-lucide-send"
      :label="t('SUPORTE_AMSHIFT.NOVO.ENVIAR')"
      :is-loading="enviando"
      :disabled="enviando"
      data-test="enviar"
      @click="enviar"
    />
  </div>
</template>
